import { supabase } from '../supabase';

const CORE_URL =
  (import.meta.env.VITE_FLUSIA_CORE_URL as string | undefined) ?? 'https://core.flusia.it';

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers = new Headers(init.headers ?? {});
  headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetch(`${CORE_URL}${path}`, { ...init, headers });
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let err = `http_${res.status}`;
    try {
      const j = (await res.json()) as { error?: unknown };
      if (typeof j.error === 'string') {
        err = j.error;
      } else if (j.error && typeof j.error === 'object') {
        // Middleware auth ritorna { error: { type, message } } — estraiamo il message.
        const e = j.error as { message?: unknown; type?: unknown };
        if (typeof e.message === 'string') err = e.message;
        else if (typeof e.type === 'string') err = e.type;
        else err = JSON.stringify(j.error);
      }
    } catch {
      // body non JSON, lascia http_xxx
    }
    throw new Error(err);
  }
  return res.json() as Promise<T>;
}

export interface EnrichmentSettings {
  id: string;
  organization_id: string;
  master_enabled: boolean;
  firmografia_enabled: boolean;
  persona_enabled: boolean;
  mobile_phone_enabled: boolean;
  mobile_phone_risk_accepted_at: string | null;
  source_meta_lead_ads_enabled: boolean;
  source_public_form_enabled: boolean;
  source_whatsapp_inbound_enabled: boolean;
  source_external_api_enabled: boolean;
  source_csv_import_enabled: boolean;
  base_legal: 'legittimo_interesse' | 'consenso_esplicito' | 'misto';
  monthly_credits_quota: number;
  monthly_credits_used: number;
  monthly_reset_at: string;
  refresh_enabled: boolean;
  refresh_interval_months: number;
  created_at: string;
  updated_at: string;
}

export interface EnrichmentRunRow {
  id: string;
  dimension: 'firmografia' | 'persona' | 'mobile_phone' | 'internal_parse';
  provider: string;
  status: string;
  credits_consumed: number;
  cost_eur_estimated: number;
  completed_at: string | null;
  error_code: string | null;
  error_message: string | null;
}

export interface FieldProvenanceRow {
  id: string;
  field_name: string;
  source: string;
  source_run_id: string | null;
  base_legal: string;
  captured_at: string;
  expires_at: string | null;
  removed_at: string | null;
  remove_reason: string | null;
}

export interface QuotaResponse {
  quota: {
    monthly_credits_quota: number;
    monthly_credits_used: number;
    monthly_reset_at?: string;
    master_enabled?: boolean;
  };
  runs_history: Array<{
    credits_consumed: number;
    cost_eur_estimated: number;
    completed_at: string | null;
    provider: string;
    dimension: string;
  }>;
}

export async function getEnrichmentSettings(): Promise<EnrichmentSettings> {
  const res = await authedFetch('/api/enrichment/settings', { method: 'GET' });
  const data = await jsonOrThrow<{ settings: EnrichmentSettings }>(res);
  return data.settings;
}

export async function patchEnrichmentSettings(
  updates: Partial<EnrichmentSettings>,
): Promise<EnrichmentSettings> {
  const res = await authedFetch('/api/enrichment/settings', {
    method: 'PATCH',
    body: JSON.stringify({ updates }),
  });
  const data = await jsonOrThrow<{ settings: EnrichmentSettings }>(res);
  return data.settings;
}

export async function triggerEnrichment(
  leadId: string,
  triggerType: 'manual' = 'manual',
): Promise<{ enqueued: boolean; reason?: string }> {
  const res = await authedFetch('/api/enrichment/trigger', {
    method: 'POST',
    body: JSON.stringify({ lead_id: leadId, trigger_type: triggerType }),
  });
  return jsonOrThrow<{ enqueued: boolean; reason?: string }>(res);
}

export async function getLeadEnrichmentStatus(leadId: string): Promise<{
  lead_id: string;
  runs: EnrichmentRunRow[];
  field_provenance: FieldProvenanceRow[];
}> {
  const res = await authedFetch(`/api/enrichment/lead/${leadId}/status`, { method: 'GET' });
  return jsonOrThrow(res);
}

export async function getEnrichmentQuota(): Promise<QuotaResponse> {
  const res = await authedFetch('/api/enrichment/quota', { method: 'GET' });
  return jsonOrThrow<QuotaResponse>(res);
}

export async function removeFieldProvenance(id: string, reason?: string): Promise<void> {
  const res = await authedFetch(`/api/enrichment/provenance/${id}/remove`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  await jsonOrThrow(res);
}

export async function getEnrichmentAuditLog(limit = 50): Promise<
  Array<{
    id: string;
    field_name: string;
    old_value: unknown;
    new_value: unknown;
    changed_by: string | null;
    changed_at: string;
  }>
> {
  const res = await authedFetch(`/api/enrichment/settings/audit?limit=${limit}`, { method: 'GET' });
  const data = await jsonOrThrow<{ audit: Array<{ id: string; field_name: string; old_value: unknown; new_value: unknown; changed_by: string | null; changed_at: string }> }>(res);
  return data.audit;
}

// ─── BYOK marketplace ──────────────────────────────────────────────────────

export interface ProviderKeyMeta {
  id: string;
  provider_key: string;
  enabled: boolean;
  has_key: boolean;
  notes: string | null;
  updated_at: string;
}

export async function listProviderKeys(): Promise<ProviderKeyMeta[]> {
  const res = await authedFetch('/api/enrichment/byok', { method: 'GET' });
  const data = await jsonOrThrow<{ providers: ProviderKeyMeta[] }>(res);
  return data.providers;
}

export async function upsertProviderKey(
  providerKey: string,
  input: { api_key: string; enabled?: boolean; notes?: string | null },
): Promise<void> {
  const res = await authedFetch(`/api/enrichment/byok/${providerKey}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  await jsonOrThrow(res);
}

export async function toggleProviderKey(providerKey: string, enabled: boolean): Promise<void> {
  const res = await authedFetch(`/api/enrichment/byok/${providerKey}/toggle`, {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  });
  await jsonOrThrow(res);
}

export async function deleteProviderKey(providerKey: string): Promise<void> {
  const res = await authedFetch(`/api/enrichment/byok/${providerKey}`, { method: 'DELETE' });
  await jsonOrThrow(res);
}
